import { http } from "@/lib/http";

import type {
  AccessApp,
  AccessAppRequest,
  AccessDevice,
  AccessGroup,
  AccessGroupRequest,
  AccessUserCreateRequest,
  AccessUserDetail,
  AccessUserUpdateRequest,
  DirectoryRead,
  IdentityProvider,
  IdentityProviderRequest,
  ProviderTest,
  SignIn,
  AccessDecision,
  AccessPolicy,
  AccessPolicyRequest,
  BreakGlassRequest,
  PolicyTestBulkResult,
  PolicyTestRequest,
  PolicyTestResult,
  AccessRequest,
  AccessSession,
  AccessUser,
  Connector,
  ConnectorCreateRequest,
  ConnectorGroup,
  ConnectorGroupRequest,
  ConnectorInstall,
  ConnectorSync,
  SiteConnector,
  SiteConnectorCreateRequest,
  SiteOffer,
  Org,
  OrgCreateRequest,
  OrgUpdateRequest,
  AccessLogFilters,
  AccessLogSearch,
  SavedLogFilter,
  SavedLogFilterRequest,
  AccessOverview,
  ConnectorInventory,
  DeviceRead,
  InventoryDevice,
  InventoryDeviceFilter,
  InventoryUser,
} from "./types";
import type { ProtectAsk, ProtectProposal } from "./protect";
import type { AccessClient, ClientPlatform } from "./getStarted";

// -- organisation ------------------------------------------------------------
/** The project's organisation, or `null` when it has none yet: asked with
 *  `missing=empty`, so "none yet" is a 204 rather than a 404 the browser logs
 *  as a failed request on every Private access page (release rehearsal R-3). */
export async function getOrg(): Promise<Org | null> {
  const resp = await http.get<Org | "">("/access/org", { params: { missing: "empty" } });
  if (resp.status === 204) return null;
  // anything else that is not an organisation (an empty 200 from a proxy, an
  // HTML page) is an error, never "none yet": that would offer Create
  if (!resp.data || typeof resp.data !== "object" || !("id" in resp.data))
    throw new Error("The organisation could not be read");
  return resp.data;
}

export async function createOrg(payload: OrgCreateRequest): Promise<Org> {
  const { data } = await http.post<Org>("/access/org", payload);
  return data;
}

export async function updateOrg(payload: OrgUpdateRequest): Promise<Org> {
  const { data } = await http.put<Org>("/access/org", payload);
  return data;
}

export async function deleteOrg(): Promise<void> {
  await http.delete("/access/org");
}

// -- connector groups and connectors -----------------------------------------
export async function listConnectorGroups(): Promise<ConnectorGroup[]> {
  const { data } = await http.get<ConnectorGroup[]>("/access/connector-groups");
  return data;
}

export async function createConnectorGroup(
  payload: ConnectorGroupRequest,
): Promise<ConnectorGroup> {
  const { data } = await http.post<ConnectorGroup>("/access/connector-groups", payload);
  return data;
}

export async function updateConnectorGroup(
  groupId: string,
  payload: ConnectorGroupRequest,
): Promise<ConnectorGroup> {
  const { data } = await http.put<ConnectorGroup>(`/access/connector-groups/${groupId}`, payload);
  return data;
}

export async function deleteConnectorGroup(groupId: string): Promise<void> {
  await http.delete(`/access/connector-groups/${groupId}`);
}

export async function listConnectors(): Promise<Connector[]> {
  const { data } = await http.get<Connector[]>("/access/connectors");
  return data;
}

export async function createConnector(payload: ConnectorCreateRequest): Promise<ConnectorInstall> {
  const { data } = await http.post<ConnectorInstall>("/access/connectors", payload);
  return data;
}

export async function newInstallBlock(connectorId: string): Promise<ConnectorInstall> {
  const { data } = await http.post<ConnectorInstall>(
    `/access/connectors/${connectorId}/install-block`,
  );
  return data;
}

export async function drainConnector(connectorId: string): Promise<Connector> {
  const { data } = await http.post<Connector>(`/access/connectors/${connectorId}/drain`);
  return data;
}

export async function resumeConnector(connectorId: string): Promise<Connector> {
  const { data } = await http.post<Connector>(`/access/connectors/${connectorId}/resume`);
  return data;
}

export async function deleteConnector(connectorId: string): Promise<void> {
  await http.delete(`/access/connectors/${connectorId}`);
}

// -- site connectors (PA-21) --------------------------------------------------
export async function listSiteConnectors(): Promise<SiteConnector[]> {
  const { data } = await http.get<SiteConnector[]>("/access/site-connectors");
  return data;
}

export async function getSiteOffer(): Promise<SiteOffer> {
  const { data } = await http.get<SiteOffer>("/access/site-connectors/availability");
  return data;
}

export async function getSiteConnector(connectorId: string): Promise<SiteConnector> {
  const { data } = await http.get<SiteConnector>(`/access/site-connectors/${connectorId}`);
  return data;
}

export async function createSiteConnector(
  payload: SiteConnectorCreateRequest,
): Promise<ConnectorInstall> {
  const { data } = await http.post<ConnectorInstall>("/access/site-connectors", payload);
  return data;
}

export async function newSiteInstallBlock(connectorId: string): Promise<ConnectorInstall> {
  const { data } = await http.post<ConnectorInstall>(
    `/access/site-connectors/${connectorId}/install-block`,
  );
  return data;
}

export async function deleteSiteConnector(connectorId: string): Promise<void> {
  await http.delete(`/access/site-connectors/${connectorId}`);
}

export async function syncConnectorApps(): Promise<ConnectorSync> {
  const { data } = await http.post<ConnectorSync>("/access/connectors/sync");
  return data;
}

// -- apps --------------------------------------------------------------------
export async function listApps(): Promise<AccessApp[]> {
  const { data } = await http.get<AccessApp[]>("/access/apps");
  return data;
}

export async function getApp(appId: string): Promise<AccessApp> {
  const { data } = await http.get<AccessApp>(`/access/apps/${appId}`);
  return data;
}

export async function createApp(payload: AccessAppRequest): Promise<AccessApp> {
  const { data } = await http.post<AccessApp>("/access/apps", payload);
  return data;
}

export async function updateApp(appId: string, payload: AccessAppRequest): Promise<AccessApp> {
  const { data } = await http.put<AccessApp>(`/access/apps/${appId}`, payload);
  return data;
}

/** Removes the app; the answer is the warning when the change was not pushed yet. */
export async function deleteApp(appId: string): Promise<string | null> {
  const response = await http.delete(`/access/apps/${appId}`);
  const warning = response.headers?.["x-access-warning"];
  return typeof warning === "string" && warning ? warning : null;
}

/** The app's access policies, in order: the first that matches decides. */
export async function setAppPolicies(appId: string, policyIds: string[]): Promise<AccessApp> {
  const { data } = await http.put<AccessApp>(`/access/apps/${appId}/policies`, {
    policy_ids: policyIds,
  });
  return data;
}

// -- access policies ---------------------------------------------------------
export async function listPolicies(): Promise<AccessPolicy[]> {
  const { data } = await http.get<AccessPolicy[]>("/access/policies");
  return data;
}

export async function getPolicy(policyId: string): Promise<AccessPolicy> {
  const { data } = await http.get<AccessPolicy>(`/access/policies/${policyId}`);
  return data;
}

export async function createPolicy(payload: AccessPolicyRequest): Promise<AccessPolicy> {
  const { data } = await http.post<AccessPolicy>("/access/policies", payload);
  return data;
}

export async function updatePolicy(
  policyId: string,
  payload: AccessPolicyRequest,
): Promise<AccessPolicy> {
  const { data } = await http.put<AccessPolicy>(`/access/policies/${policyId}`, payload);
  return data;
}

export async function deletePolicy(policyId: string): Promise<void> {
  await http.delete(`/access/policies/${policyId}`);
}

// -- decisions, break-glass and the tester (PA-8, PA-9; ops doc §12) ---------
/** Each user's current decision on the app. */
export async function listAppDecisions(appId: string): Promise<AccessDecision[]> {
  const { data } = await http.get<AccessDecision[]>(`/access/apps/${appId}/decisions`);
  return data;
}

/** The user's current decision on every app. */
export async function listUserDecisions(userId: string): Promise<AccessDecision[]> {
  const { data } = await http.get<AccessDecision[]>(`/access/users/${userId}/decisions`);
  return data;
}

/** Lets one user reach the app whatever its policies say, for at most 24 h;
 *  the answer is the app, its open break-glasses under `break_glass`. */
export async function openBreakGlass(appId: string, payload: BreakGlassRequest): Promise<AccessApp> {
  const { data } = await http.post<AccessApp>(`/access/apps/${appId}/break-glass`, payload);
  return data;
}

/** Ends the user's open break-glass now; the answer is the warning when the
 *  change was not pushed yet. */
export async function closeBreakGlass(appId: string, user: string): Promise<string | null> {
  const response = await http.delete(`/access/apps/${appId}/break-glass`, { params: { user } });
  const warning = response.headers?.["x-access-warning"];
  return typeof warning === "string" && warning ? warning : null;
}

/** What the policies would decide (PA-9): one answer for a user or a group,
 *  a table for every user who signed in within 30 days. */
export async function testPolicies(
  payload: PolicyTestRequest,
): Promise<PolicyTestResult | PolicyTestBulkResult> {
  const { data } = await http.post<PolicyTestResult | PolicyTestBulkResult>(
    "/access/policies/test",
    payload,
  );
  return data;
}

// -- inventories -------------------------------------------------------------
export async function listUsers(): Promise<AccessUser[]> {
  const { data } = await http.get<AccessUser[]>("/access/users");
  return data;
}

export async function listDevices(): Promise<AccessDevice[]> {
  const { data } = await http.get<AccessDevice[]>("/access/devices");
  return data;
}

export async function listSessions(): Promise<AccessSession[]> {
  const { data } = await http.get<AccessSession[]>("/access/sessions");
  return data;
}

export async function listRequests(): Promise<AccessRequest[]> {
  const { data } = await http.get<AccessRequest[]>("/access/requests");
  return data;
}

// -- identity (PA-4): sign-in, users, groups, identity providers --------------
export async function getSignIn(): Promise<SignIn> {
  const { data } = await http.get<SignIn>("/access/org/sign-in");
  return data;
}

export async function getUser(userId: string): Promise<AccessUserDetail> {
  const { data } = await http.get<AccessUserDetail>(`/access/users/${userId}`);
  return data;
}

export async function createUser(payload: AccessUserCreateRequest): Promise<AccessUserDetail> {
  const { data } = await http.post<AccessUserDetail>("/access/users", payload);
  return data;
}

export async function updateUser(
  userId: string,
  payload: AccessUserUpdateRequest,
): Promise<AccessUserDetail> {
  const { data } = await http.put<AccessUserDetail>(`/access/users/${userId}`, payload);
  return data;
}

export async function deleteUser(userId: string): Promise<void> {
  await http.delete(`/access/users/${userId}`);
}

/** Read the directory back now: users who signed in through an identity
 *  provider appear, users removed there go. */
export async function readDirectory(): Promise<DirectoryRead> {
  const { data } = await http.post<DirectoryRead>("/access/users/read");
  return data;
}

export async function listGroups(): Promise<AccessGroup[]> {
  const { data } = await http.get<AccessGroup[]>("/access/groups");
  return data;
}

export async function createGroup(payload: AccessGroupRequest): Promise<AccessGroup> {
  const { data } = await http.post<AccessGroup>("/access/groups", payload);
  return data;
}

export async function updateGroup(
  groupId: string,
  description: string | null,
): Promise<AccessGroup> {
  const { data } = await http.put<AccessGroup>(`/access/groups/${groupId}`, { description });
  return data;
}

export async function deleteGroup(groupId: string): Promise<void> {
  await http.delete(`/access/groups/${groupId}`);
}

export async function listIdentityProviders(): Promise<IdentityProvider[]> {
  const { data } = await http.get<IdentityProvider[]>("/access/org/identity-providers");
  return data;
}

export async function getIdentityProvider(providerId: string): Promise<IdentityProvider> {
  const { data } = await http.get<IdentityProvider>(`/access/org/identity-providers/${providerId}`);
  return data;
}

export async function createIdentityProvider(
  payload: IdentityProviderRequest,
): Promise<IdentityProvider> {
  const { data } = await http.post<IdentityProvider>("/access/org/identity-providers", payload);
  return data;
}

export async function updateIdentityProvider(
  providerId: string,
  payload: IdentityProviderRequest,
): Promise<IdentityProvider> {
  const { data } = await http.put<IdentityProvider>(
    `/access/org/identity-providers/${providerId}`,
    payload,
  );
  return data;
}

export async function deleteIdentityProvider(providerId: string): Promise<void> {
  await http.delete(`/access/org/identity-providers/${providerId}`);
}

/** Check the provider and get the address to sign in at, to try it. */
export async function startProviderTest(providerId: string): Promise<ProviderTest> {
  const { data } = await http.post<ProviderTest>(
    `/access/org/identity-providers/${providerId}/test`,
  );
  return data;
}

/** What became of the sign-in that was tried. */
export async function readProviderTest(providerId: string): Promise<ProviderTest> {
  const { data } = await http.get<ProviderTest>(
    `/access/org/identity-providers/${providerId}/test`,
  );
  return data;
}

// -- PA-13: inventories and the overview (ops doc §13.2) ---------------------
/** Active users, sessions, blocks by reason, unhealthy connectors. */
export async function getAccessOverview(hours = 24): Promise<AccessOverview> {
  const { data } = await http.get<AccessOverview>("/access/overview", { params: { hours } });
  return data;
}

export async function listInventoryUsers(): Promise<InventoryUser[]> {
  const { data } = await http.get<InventoryUser[]>("/access/inventory/users");
  return data;
}

export async function listInventoryDevices(
  filter: InventoryDeviceFilter = {},
): Promise<InventoryDevice[]> {
  const { data } = await http.get<InventoryDevice[]>("/access/inventory/devices", {
    params: filter,
  });
  return data;
}

// -- the client downloads (PA-11, Get started; ops doc §21) --------------------
export async function listClients(): Promise<AccessClient[]> {
  const { data } = await http.get<AccessClient[]>("/access/clients");
  return data;
}

/** The platform's package as a file (Linux: the .deb). */
export async function downloadClient(platform: ClientPlatform): Promise<Blob> {
  const { data } = await http.get<Blob>(`/access/clients/${platform}/download`, {
    responseType: "blob",
  });
  return data;
}

export async function getConnectorInventory(): Promise<ConnectorInventory> {
  const { data } = await http.get<ConnectorInventory>("/access/inventory/connectors");
  return data;
}

/** Read the users' devices from the organisation's network now. */
export async function readDevices(): Promise<DeviceRead> {
  const { data } = await http.post<DeviceRead>("/access/devices/read");
  return data;
}

// -- the access log (PA-13, ops doc §13.1) -----------------------------------
/** The query string of a search: the filters, the window as `since`. */
export function accessLogParams(
  filters: AccessLogFilters,
  since?: string,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (key === "window_minutes" || value === undefined || value === null || value === "") continue;
    out[key] = value as string;
  }
  if (since) out.since = since;
  return out;
}

export async function searchAccessLog(
  filters: AccessLogFilters,
  since?: string,
  limit = 500,
): Promise<AccessLogSearch> {
  const { data } = await http.get<AccessLogSearch>("/access/logs", {
    params: { ...accessLogParams(filters, since), limit },
  });
  return data;
}

/** The same search as CSV, the chosen columns in order. */
export async function exportAccessLog(
  filters: AccessLogFilters,
  columns: string[],
  since?: string,
): Promise<string> {
  const { data } = await http.get<string>("/access/logs/export", {
    params: { ...accessLogParams(filters, since), columns: columns.join(",") },
    responseType: "text",
  });
  return data;
}

export async function listSavedLogFilters(): Promise<SavedLogFilter[]> {
  const { data } = await http.get<SavedLogFilter[]>("/access/logs/filters");
  return data;
}

export async function createSavedLogFilter(
  payload: SavedLogFilterRequest,
): Promise<SavedLogFilter> {
  const { data } = await http.post<SavedLogFilter>("/access/logs/filters", payload);
  return data;
}

export async function updateSavedLogFilter(
  filterId: string,
  payload: SavedLogFilterRequest,
): Promise<SavedLogFilter> {
  const { data } = await http.put<SavedLogFilter>(`/access/logs/filters/${filterId}`, payload);
  return data;
}

export async function deleteSavedLogFilter(filterId: string): Promise<void> {
  await http.delete(`/access/logs/filters/${filterId}`);
}

// -- SCIM provisioning (PA-18) ------------------------------------------------
import type { ScimSettings, ScimTokenIssued } from "./types";

export async function getScimSettings(): Promise<ScimSettings> {
  const { data } = await http.get<ScimSettings>("/access/org/scim");
  return data;
}

export async function createScimToken(name: string): Promise<ScimTokenIssued> {
  const { data } = await http.post<ScimTokenIssued>("/access/org/scim/tokens", { name });
  return data;
}

export async function rotateScimToken(id: string): Promise<ScimTokenIssued> {
  const { data } = await http.post<ScimTokenIssued>(`/access/org/scim/tokens/${id}/rotate`);
  return data;
}

export async function revokeScimToken(id: string): Promise<void> {
  await http.delete(`/access/org/scim/tokens/${id}`);
}

// -- remote-access VPN profiles (PA-20; shapes in vpnTypes.ts) ---------------- //
import type { VpnProfileIssued, VpnProfileList } from "./vpnTypes";

export async function listVpnProfiles(all = false): Promise<VpnProfileList> {
  const { data } = await http.get<VpnProfileList>("/access/vpn/profiles", {
    params: all ? { all: true } : undefined,
  });
  return data;
}

export async function createVpnProfile(deviceName: string): Promise<VpnProfileIssued> {
  const { data } = await http.post<VpnProfileIssued>("/access/vpn/profiles", {
    device_name: deviceName,
  });
  return data;
}

export async function revokeVpnProfile(profileId: string): Promise<void> {
  await http.delete(`/access/vpn/profiles/${profileId}`);
}

// -- VPN discovery (PA-22) ----------------------------------------------------
import type {
  DiscoveryCreateAppRequest,
  DiscoveryCreateAppResponse,
  DiscoveryDraft,
  DiscoveryRowRef,
  VpnConversion,
  VpnDiscovery,
} from "./types";

export async function getVpnDiscovery(hours: number): Promise<VpnDiscovery> {
  const { data } = await http.get<VpnDiscovery>("/access/vpn/discovery", { params: { hours } });
  return data;
}

export async function getVpnConversion(days: number): Promise<VpnConversion> {
  const { data } = await http.get<VpnConversion>("/access/vpn/discovery/conversion", {
    params: { days },
  });
  return data;
}

export async function draftVpnDiscoveryApp(
  rows: DiscoveryRowRef[],
  hours: number,
): Promise<DiscoveryDraft> {
  const { data } = await http.post<DiscoveryDraft>("/access/vpn/discovery/draft", {
    rows,
    hours,
  });
  return data;
}

export async function createVpnDiscoveryApp(
  payload: DiscoveryCreateAppRequest,
): Promise<DiscoveryCreateAppResponse> {
  const { data } = await http.post<DiscoveryCreateAppResponse>(
    "/access/vpn/discovery/apps",
    payload,
  );
  return data;
}

// -- VPN gateway (PA-20): made when an app turns VPN on, read-only here ------------------ //
import type { VpnGateway } from "./types";

export async function getVpnGateway(): Promise<VpnGateway> {
  const { data } = await http.get<VpnGateway>("/access/vpn/gateway");
  return data;
}

// -- Protect with Private access (PA-14; ops doc §23) -------------------------
export async function getProtectProposal(ask: ProtectAsk): Promise<ProtectProposal> {
  const { data } = await http.get<ProtectProposal>("/access/protect/proposal", {
    params: {
      kind: ask.kind,
      id: ask.id,
      zone_id: ask.zoneId ?? undefined,
      network_id: ask.networkId ?? undefined,
    },
  });
  return data;
}
